/**
 * Luxe · Scene3DBuilder (Phase 2B-1).
 *
 * Deterministic Three.js scene from the canonical DesignScene. Every
 * geometry, material and light traces back to a single canonical
 * value — no invented geometry, no invented rates.
 */
import * as THREE from "three";
import { mmToM } from "./coordinateAdapter";
import { buildCabinetGroup } from "./Cabinet3DBuilder";

const CABINET_TYPES = new Set(["base_cabinet", "floating_cabinet", "tall_cabinet", "drawer_unit"]);
const EQUIPMENT_TYPES = new Set(["tv", "soundbar", "speaker"]);
const LIGHTING_TYPES = new Set(["downlight", "wall_light", "led_strip", "perimeter_led"]);

// Neutral colour helper (matches Cabinet3DBuilder hash for consistency).
const _colour = (mid) => {
  if (!mid) return 0x777777;
  let h = 0;
  for (let i = 0; i < mid.length; i++) h = (h * 31 + mid.charCodeAt(i)) | 0;
  return h & 0xffffff;
};

/**
 * Build a deterministic THREE.Scene from a DesignScene snapshot.
 * @param {object} scene    - schematic snapshot ({ items, walls, ... })
 * @param {object} project  - project doc (wall dims, render env)
 * @param {object} sceneVersion - optional Design Option (frozen)
 * @param {object} pinsByMaterialId - resolved visual asset pins
 */
export function buildLuxeScene(scene, project, sceneVersion, pinsByMaterialId = {}) {
  const three = new THREE.Scene();
  three.name = "LuxeDesignScene";
  three.background = _environmentBackground(project?.render_environment || "natural_daylight");
  // ── Ambient + environment lighting (presentation, not physical) ──
  _addEnvironmentLighting(three, project?.render_environment || "natural_daylight");
  // ── Room / wall ─────────────────────────────────────────────
  const wallWMm = Number(project?.wall_width_mm)  || Number(scene?.wall_width_mm)  || 4000;
  const wallHMm = Number(project?.wall_height_mm) || Number(scene?.wall_height_mm) || 2700;
  three.add(_buildWall(wallWMm, wallHMm));
  three.add(_buildFloor(wallWMm));
  // ── Items ────────────────────────────────────────────────────
  const items = scene?.items || [];
  items.forEach((it) => {
    if (CABINET_TYPES.has(it.type)) {
      three.add(buildCabinetGroup(it, { pinsByMaterialId }));
    } else if (it.type === "fireplace") {
      three.add(_buildFireplace(it));
    } else if (EQUIPMENT_TYPES.has(it.type)) {
      three.add(_buildEquipment(it));
    } else if (LIGHTING_TYPES.has(it.type)) {
      three.add(_buildLighting(it));
    }
  });
  return three;
}

function _buildWall(wMm, hMm) {
  const w = mmToM(wMm), h = mmToM(hMm);
  const g = new THREE.PlaneGeometry(w, h);
  const m = new THREE.MeshStandardMaterial({ color: 0xf3efe8, roughness: 0.95 });
  const wall = new THREE.Mesh(g, m);
  wall.position.set(w / 2, h / 2, 0);
  wall.name = "wall";
  wall.userData = { scope: "wall", w_mm: wMm, h_mm: hMm };
  return wall;
}
function _buildFloor(wMm) {
  const w = mmToM(wMm);
  const g = new THREE.PlaneGeometry(w * 2, w * 2);
  const m = new THREE.MeshStandardMaterial({ color: 0x2b2724, roughness: 0.9 });
  const f = new THREE.Mesh(g, m);
  f.rotation.x = -Math.PI / 2;
  f.position.set(w / 2, 0, w / 2);
  f.name = "floor";
  return f;
}

function _buildFireplace(it) {
  const g = new THREE.Group();
  g.name = `fireplace:${it.id}`;
  g.userData = { item_id: it.id, component_type: "fireplace",
                 visible_faces: it.fireplace_config?.visible_faces || ["front"] };
  const w = mmToM(it.width), h = mmToM(it.height), d = mmToM(it.depth || 400);
  const bodyGeom = new THREE.BoxGeometry(w, h, d);
  const body = new THREE.Mesh(bodyGeom, new THREE.MeshStandardMaterial({
    color: 0x1a1a1a, roughness: 0.4, metalness: 0.3, emissive: 0x552211, emissiveIntensity: 0.15,
  }));
  body.position.set(mmToM(it.x) + w / 2, mmToM(it.y) + h / 2, mmToM(it.z || 0) + d / 2);
  body.userData = { scope: "fireplace_body" };
  g.add(body);
  return g;
}

function _buildEquipment(it) {
  const g = new THREE.Group();
  g.name = `${it.type}:${it.id}`;
  g.userData = { item_id: it.id, component_type: it.type };
  const w = mmToM(it.width), h = mmToM(it.height), d = mmToM(it.depth || 60);
  const geom = new THREE.BoxGeometry(w, h, d);
  const mat = new THREE.MeshStandardMaterial({
    color: it.type === "tv" ? 0x111111 : _colour(it.type),
    roughness: it.type === "tv" ? 0.2 : 0.6,
    metalness: it.type === "tv" ? 0.1 : 0.0,
  });
  const m = new THREE.Mesh(geom, mat);
  m.position.set(mmToM(it.x) + w / 2, mmToM(it.y) + h / 2, mmToM(it.z || 0) + d / 2);
  m.name = `${it.type}:${it.id}:body`;
  m.userData = { scope: it.type };
  g.add(m);
  return g;
}

// ── Lighting3D helpers ──────────────────────────────────────────
// Canonical Kelvin → RGB approximation (a deterministic adapter, not
// physically calibrated). Also maps brightness 0..100 to Three.js
// intensity in the 0..2 range. Real physical calibration lives in a
// future dedicated Lighting3DBuilder module.
function _kelvinToColour(kelvin) {
  const k = Math.max(1500, Math.min(6500, Number(kelvin) || 3000));
  // Simple linear ramp: 2200K → warm (#ffb070), 6500K → cool (#ffffff)
  const t = (k - 2200) / (6500 - 2200);
  const r = 1.0;
  const g = 0.6 + 0.4 * t;
  const b = 0.35 + 0.65 * t;
  return new THREE.Color(r, g, b);
}
function _buildLighting(it) {
  const g = new THREE.Group();
  g.name = `${it.type}:${it.id}`;
  g.userData = { item_id: it.id, component_type: it.type };
  const l = it.lighting || {};
  const on = l.on !== false;
  if (!on) return g;
  const intensity = ((Number(l.brightness_pct) || 60) / 100) * 1.5;
  const colour = _kelvinToColour(l.kelvin || 3000);
  const pos = new THREE.Vector3(
    mmToM(it.x) + mmToM(it.width  || 100) / 2,
    mmToM(it.y) + mmToM(it.height || 100) / 2,
    mmToM(it.z || 0),
  );
  if (it.type === "downlight") {
    const angleDeg = Number(l.beam_angle_deg) || 36;
    const light = new THREE.SpotLight(colour, intensity, mmToM(4000), (angleDeg * Math.PI / 180) / 2, 0.25, 1);
    light.position.copy(pos);
    light.target.position.set(pos.x, 0, pos.z + mmToM(200));
    g.add(light);
    g.add(light.target);
  } else {
    // led_strip / perimeter_led / wall_light — soft point light.
    const light = new THREE.PointLight(colour, intensity * 0.6, mmToM(3000), 2);
    light.position.copy(pos);
    g.add(light);
  }
  return g;
}

// ── Render environment presentation ─────────────────────────────
function _environmentBackground(envKey) {
  const bg = {
    natural_daylight:    0xf6f4ef,
    evening:             0x1e1a17,
    warm_architectural:  0x2a2622,
    neutral_studio:      0x999999,
    client_hero:         0x0f0d0b,
  }[envKey] || 0xf3efe8;
  return new THREE.Color(bg);
}
function _addEnvironmentLighting(scene, envKey) {
  const ambientIntensity = {
    natural_daylight:   0.75,
    evening:            0.15,
    warm_architectural: 0.35,
    neutral_studio:     0.6,
    client_hero:        0.10,
  }[envKey] || 0.5;
  const ambientColour = {
    natural_daylight:   0xffffff,
    evening:            0xffcc99,
    warm_architectural: 0xffe0b3,
    neutral_studio:     0xffffff,
    client_hero:        0xffb080,
  }[envKey] || 0xffffff;
  scene.add(new THREE.AmbientLight(ambientColour, ambientIntensity));
  // Key light: soft directional so front geometry is legible.
  const dir = new THREE.DirectionalLight(0xffffff, envKey === "client_hero" ? 0.4 : 0.6);
  dir.position.set(4, 3, 5);
  scene.add(dir);
}
